"use client";

import { useState, useEffect, useCallback } from "react";
import { useRouter } from "@tanstack/react-router";
import {
  IconArrowLeft,
  IconLoader2,
  IconBuilding,
  IconMapPin,
  IconDotsVertical,
  IconTrash,
} from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DeleteVenueDialog } from "@/components/delete-venue-dialog";
import { getOrganisationById } from "@/services/organisation";
import { getVenues } from "@/services/venue";
import { Organisation, Venue } from "@/types";
import { toast } from "sonner";

interface OrganisationDetailClientProps {
  organisationId: string;
}

export default function OrganisationDetailClient({
  organisationId,
}: OrganisationDetailClientProps) {
  const router = useRouter();
  const [organisation, setOrganisation] = useState<Organisation | null>(null);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [totalVenues, setTotalVenues] = useState(0);
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [isLoadingVenues, setIsLoadingVenues] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize] = useState(10);
  const [deletingVenue, setDeletingVenue] = useState<Venue | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Fetch organisation details on mount
  useEffect(() => {
    const fetchOrganisation = async () => {
      if (!organisationId) {
        setError("Organisation ID is required");
        setIsLoadingPage(false);
        return;
      }

      try {
        setIsLoadingPage(true);
        const organisationData = await getOrganisationById(organisationId);
        setOrganisation(organisationData);
      } catch (err) {
        console.error("Error fetching organisation:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load organisation data"
        );
        toast.error("Failed to load organisation details");
      } finally {
        setIsLoadingPage(false);
      }
    };

    fetchOrganisation();
  }, [organisationId]);

  // Set document title with truncated org name
  useEffect(() => {
    if (!organisation) return;
    const maxLen = 25;
    const titleName =
      organisation.name.length > maxLen
        ? `${organisation.name.slice(0, maxLen)}…`
        : organisation.name;
    document.title = `${titleName} | Relivable | Venue Admin`;
    return () => {
      document.title = "Relivable | Venue Admin";
    };
  }, [organisation]);

  // Fetch venues when page changes
  useEffect(() => {
    const fetchVenues = async () => {
      if (!organisationId) return;

      try {
        setIsLoadingVenues(true);
        const venuesResponse = await getVenues({
          pageNumber: pageIndex + 1, // API uses 1-based page numbers
          pageSize: pageSize,
          organisationId: organisationId,
        });
        setVenues(venuesResponse.data);
        setTotalVenues(venuesResponse.count);
      } catch (venueErr) {
        console.error("Error fetching organisation venues:", venueErr);
        setVenues([]);
        setTotalVenues(0);
      } finally {
        setIsLoadingVenues(false);
      }
    };

    fetchVenues();
  }, [organisationId, pageIndex, pageSize]);

  const handleVenueClick = (venueId: string) => {
    router.navigate({ to: `/venues/${venueId}` });
  };

  // Return to the organisations list. Prefer browser history so the list
  // restores the exact page/search the user came from; fall back to a plain
  // navigate when there is no in-app history (e.g. opened directly).
  const handleBackToOrganisations = useCallback(() => {
    if (window.history.length > 1) {
      router.history.back();
    } else {
      router.navigate({
        to: "/organisations",
        search: { page: 1, pageSize: 10, search: "" },
      });
    }
  }, [router]);

  const handleDeleteClick = (venue: Venue) => {
    setDeletingVenue(venue);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteSuccess = () => {
    setIsDeleteDialogOpen(false);
    setDeletingVenue(null);
    const fetchVenues = async () => {
      if (!organisationId) return;
      try {
        setIsLoadingVenues(true);
        const venuesResponse = await getVenues({
          pageNumber: pageIndex + 1,
          pageSize: pageSize,
          organisationId: organisationId,
        });
        setVenues(venuesResponse.data);
        setTotalVenues(venuesResponse.count);
      } catch (venueErr) {
        console.error("Error fetching organisation venues:", venueErr);
        setVenues([]);
        setTotalVenues(0);
      } finally {
        setIsLoadingVenues(false);
      }
    };
    fetchVenues();
  };

  const formatAddress = (venue: Venue): string => {
    if (!venue.address) return "-";
    const { addressLine1, city, state, zipcode } = venue.address;
    return `${addressLine1}, ${city}, ${state} ${zipcode}`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (isLoadingPage) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <IconLoader2 className="size-8 animate-spin text-primary" />
          <p className="text-muted-foreground">
            Loading organisation details...
          </p>
        </div>
      </div>
    );
  }

  if (error || !organisation) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="max-w-md text-center">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6">
            <h2 className="mb-2 text-lg font-semibold text-destructive">
              Error
            </h2>
            <p className="text-sm text-destructive">
              {error || "Organisation not found"}
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={handleBackToOrganisations}
            >
              Back to Organisations
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
      <div className="flex min-w-0 flex-1 flex-col gap-6 overflow-hidden p-4 md:p-6">
        {/* Header */}
        <div className="flex min-w-0 items-center justify-between gap-4">
          <div className="flex min-w-0 flex-1 items-center gap-4 overflow-hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBackToOrganisations}
              className="shrink-0"
            >
              <IconArrowLeft className="size-5" />
              <span className="sr-only">Back to organisations</span>
            </Button>
            <div className="min-w-0 flex-1">
              <h1
                className="min-w-0 max-w-md truncate text-3xl font-bold tracking-tight"
                title={organisation.name}
              >
                {organisation.name}
              </h1>
              <p className="text-muted-foreground mt-1">
                View and manage organisation details and venues
              </p>
            </div>
          </div>
        </div>

        {/* Organisation Details Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {/* Organisation Name Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Organisation
              </CardTitle>
              <IconBuilding className="size-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div
                className="min-w-0 max-w-md truncate text-2xl font-bold"
                title={organisation.name}
              >
                {organisation.name}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Created {formatDate(organisation.createdAt)}
              </p>
            </CardContent>
          </Card>

          {/* Venue Count Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Venues
              </CardTitle>
              <IconMapPin className="size-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalVenues}</div>
              <p className="text-xs text-muted-foreground">
                {totalVenues === 1 ? "venue" : "venues"} registered
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Venues Table */}
        <Card>
          <CardHeader>
            <CardTitle>Venues</CardTitle>
          </CardHeader>
          <CardContent>
            {venues.length === 0 && !isLoadingVenues ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-12">
                <p className="text-muted-foreground mb-4">
                  No venues found for this organisation
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-hidden rounded-lg border">
                  <Table>
                    <TableHeader className="bg-muted">
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Address</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="w-[100px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isLoadingVenues ? (
                        <TableRow>
                          <TableCell colSpan={4} className="h-24 text-center">
                            <div className="flex items-center justify-center">
                              <IconLoader2 className="mr-2 size-4 animate-spin" />
                              <span className="text-muted-foreground">
                                Loading venues...
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        venues.map((venue) => (
                          <TableRow key={venue.id}>
                            <TableCell className="font-medium">
                              {venue.name}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatAddress(venue)}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatDate(venue.createdAt)}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleVenueClick(venue.id)}
                                >
                                  View
                                </Button>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      className="data-[state=open]:bg-muted size-8"
                                      size="icon"
                                    >
                                      <IconDotsVertical className="size-4" />
                                      <span className="sr-only">Open menu</span>
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-40">
                                    <DropdownMenuItem
                                      variant="destructive"
                                      onClick={() => handleDeleteClick(venue)}
                                    >
                                      <IconTrash className="mr-2 size-4" />
                                      Remove
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {totalVenues > 0 && (
                  <div className="flex items-center justify-between px-2 mt-4">
                    <div className="text-muted-foreground flex-1 text-sm">
                      Showing {venues.length > 0 ? pageIndex * pageSize + 1 : 0}{" "}
                      to {Math.min((pageIndex + 1) * pageSize, totalVenues)} of{" "}
                      {totalVenues} venues
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPageIndex((prev) => prev - 1)}
                        disabled={pageIndex === 0 || isLoadingVenues}
                      >
                        Previous
                      </Button>
                      <div className="text-sm font-medium">
                        Page {pageIndex + 1} of{" "}
                        {Math.ceil(totalVenues / pageSize)}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPageIndex((prev) => prev + 1)}
                        disabled={
                          pageIndex >= Math.ceil(totalVenues / pageSize) - 1 ||
                          isLoadingVenues
                        }
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {deletingVenue && (
          <DeleteVenueDialog
            open={isDeleteDialogOpen}
            onOpenChange={setIsDeleteDialogOpen}
            venueId={deletingVenue.id}
            venueName={deletingVenue.name}
            onSuccess={handleDeleteSuccess}
          />
        )}
      </div>
    </div>
  );
}
